import React, { useEffect, useMemo, useRef, useState } from "react";

const normalizeWord = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[.,!?;:()[\]{}"“”'`]+$/g, "");
};

const getSegmentPreview = (targetSegment, maxWords = 6) => {
  const words = Array.isArray(targetSegment?.words)
    ? targetSegment.words
        .map((word) => String(word?.text ?? "").trim())
        .filter(Boolean)
    : [];

  if (!words.length) return "";

  const visibleWords = words.slice(0, maxWords);
  const preview = visibleWords.join(" ");

  return words.length > maxWords
    ? `${preview}…`
    : preview;
};

const getWordScoreColor = (score, errorType) => {
  if (errorType === "Omission") return "bg-gray-400";
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getPhonemeDotColor = (score) => {
  if (typeof score !== "number") return "bg-gray-400";
  if (score >= 80) return "bg-green-500";
  if (score >= 40) return "bg-yellow-500";
  return "bg-red-500";
};

const getWordAssessmentClasses = (score, errorType) => {
  if (errorType === "Omission") {
    return "bg-gray-50 border-gray-300 text-gray-800";
  }

  if (typeof score !== "number") {
    return "bg-white/90 border-slate-200/90 text-gray-800";
  }

  if (score >= 80) {
    return "bg-green-50 border-green-200 text-gray-800";
  }

  if (score >= 40) {
    return "bg-yellow-50 border-yellow-200 text-gray-800";
  }

  return "bg-red-50 border-red-200 text-gray-800";
};

const getActiveRingClass = (practiceMode) => {
  if (practiceMode === "shadowing") {
    return "ring-2 ring-orange-500 border-orange-300 shadow-lg shadow-orange-500/20 scale-105";
  }

  if (practiceMode === "echoing") {
    return "ring-2 ring-blue-500 border-blue-300 shadow-lg shadow-blue-500/20 scale-105";
  }

  return "ring-2 ring-emerald-500 border-emerald-300 shadow-lg shadow-emerald-500/20 scale-105";
};

const WORD_DENSITY_STYLES = {
  comfortable: {
    lane: "gap-2",
    stage:
      "min-h-[116px] px-14 pb-3 pt-[58px] sm:min-h-[116px] sm:px-36 sm:pb-4 sm:pt-[60px]",
    card:
      "min-w-[68px] gap-0.5 rounded-xl px-3.5 py-2.5 text-lg sm:text-xl",
    indicator: "h-2 gap-1",
    phonemeDot: "h-2 w-2",
    scoreDot: "h-2.5 w-2.5",
    hiddenLane: "min-h-[72px] gap-2",
    hiddenCard: "h-[50px] rounded-xl",
    hiddenMinWidth: 68,
    hiddenMaxWidth: 180,
    hiddenCharWidth: 11,
    hiddenPadding: 28,
  },
  compact: {
    lane: "gap-1.5",
    stage:
      "min-h-[102px] px-14 pb-2.5 pt-[56px] sm:px-36 sm:pt-[56px]",
    card:
      "min-w-[54px] gap-0.5 rounded-xl px-2.5 py-2 text-base sm:text-lg",
    indicator: "h-1.5 gap-0.5",
    phonemeDot: "h-1.5 w-1.5",
    scoreDot: "h-2 w-2",
    hiddenLane: "min-h-[62px] gap-1.5",
    hiddenCard: "h-[44px] rounded-xl",
    hiddenMinWidth: 54,
    hiddenMaxWidth: 160,
    hiddenCharWidth: 9.5,
    hiddenPadding: 24,
  },
  dense: {
    lane: "gap-1",
    stage:
      "min-h-[90px] px-12 pb-2 pt-[56px] sm:px-32 sm:pt-[52px]",
    card:
      "min-w-[42px] gap-0.5 rounded-lg px-2 py-1.5 text-sm sm:text-base",
    indicator: "h-1.5 gap-0.5",
    phonemeDot: "h-1.5 w-1.5",
    scoreDot: "h-1.5 w-1.5",
    hiddenLane: "min-h-[52px] gap-1",
    hiddenCard: "h-[38px] rounded-lg",
    hiddenMinWidth: 42,
    hiddenMaxWidth: 144,
    hiddenCharWidth: 8.5,
    hiddenPadding: 20,
  },
  ultra: {
    lane: "gap-[3px]",
    stage:
      "min-h-[76px] px-12 pb-1 pt-[56px] sm:px-32 sm:pt-[47px]",
    card:
      "min-w-[30px] gap-0 rounded-md px-1.5 py-0.5 text-xs sm:text-sm",
    indicator: "h-1 gap-0.5",
    phonemeDot: "h-1.5 w-1.5",
    scoreDot: "h-1.5 w-1.5",
    hiddenLane: "min-h-[44px] gap-1",
    hiddenCard: "h-[34px] rounded-lg",
    hiddenMinWidth: 34,
    hiddenMaxWidth: 128,
    hiddenCharWidth: 7.5,
    hiddenPadding: 16,
  },
};

const getAdaptiveWordDensity = ({
  words,
  fitViewport,
  compactViewport,
  viewportWidth,
  viewportHeight,
}) => {
  if (!fitViewport) return "comfortable";

  const normalizedWords = Array.isArray(words) ? words : [];
  const wordCount = normalizedWords.length;
  const characterCount = normalizedWords.reduce(
    (total, word) => total + String(word?.text || "").trim().length,
    0
  );
  const longestWord = normalizedWords.reduce(
    (longest, word) =>
      Math.max(longest, String(word?.text || "").trim().length),
    0
  );

  // Character count catches sentences made of a few unusually long words,
  // while word count catches many short function words. The longest-word term
  // avoids oversized cards dominating an otherwise short sentence.
  const heightBias =
    viewportHeight < 600
      ? 9
      : viewportHeight < 680
      ? 6
      : viewportHeight < 760
      ? 3
      : 0;

  const densityScore =
    wordCount +
    characterCount / 11 +
    Math.max(0, longestWord - 10) / 2.5 +
    heightBias;

  if (viewportWidth < 480) {
    if (densityScore >= 28 || wordCount >= 18) return "ultra";
    if (densityScore >= 18 || wordCount >= 12) return "dense";
    if (densityScore >= 11 || wordCount >= 8) return "compact";
    return "comfortable";
  }

  if (viewportWidth < 768) {
    if (densityScore >= 36 || wordCount >= 24) return "ultra";
    if (densityScore >= 24 || wordCount >= 16) return "dense";
    if (densityScore >= 15 || wordCount >= 10) return "compact";
    return "comfortable";
  }

  if (viewportWidth < 1280) {
    if (densityScore >= 52 || wordCount >= 34) return "ultra";
    if (densityScore >= 36 || wordCount >= 24) return "dense";
    if (densityScore >= 23 || wordCount >= 16) return "compact";
    return "comfortable";
  }

  const compactBias = compactViewport ? 6 : 0;
  const adjustedScore = densityScore + compactBias;

  if (adjustedScore >= 58 || wordCount >= 40) return "ultra";
  if (adjustedScore >= 38 || wordCount >= 27) return "dense";
  if (adjustedScore >= 24 || wordCount >= 17) return "compact";
  return "comfortable";
};

const buildSegmentAssessmentMap = (segmentWords = [], assessmentWords = []) => {
  let pointer = 0;

  return segmentWords.map((item) => {
    const segmentText = normalizeWord(item?.text);

    if (!segmentText) return null;

    for (let i = pointer; i < assessmentWords.length; i++) {
      if (normalizeWord(assessmentWords[i]?.Word) === segmentText) {
        pointer = i + 1;
        return assessmentWords[i];
      }
    }

    const fallback =
      assessmentWords.find(
        (word) => normalizeWord(word?.Word) === segmentText
      ) || null;

    return fallback;
  });
};

const SentencePracticeBlock = ({
  segment,
  segments,
  assessment,
  renderHeaderIdentity,
  renderGuidePill,
  renderSentenceSharePill,
  renderCommentsPill,
  isMetadataNoHelp,
  setShowExerciseHelp,
  handlePreviousSegment,
  handleNextSegment,
  handleSelectSegment,
  showCenterCue,
  practiceMode,
  echoingPhase,
  isCueActive,
  recordingStatus,
  cuePulseCount,
  echoingPulseCount,
  currentWord,
  handleWordPlay,
  openWordMenu,
  handleRangePlay,
  showWords = true,
  enableWordActions = true,
  isPlayingSegment = false,
  showWordLane = true,
  fitViewport = false,
  compactViewport = false,
  viewportHeight = 900,
}) => {
  const selectionAreaRef = useRef(null);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === "undefined" ? 1440 : window.innerWidth
  );

  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  const [showSelectionMenu, setShowSelectionMenu] = useState(false);
  const [previewSentence, setPreviewSentence] = useState(null);
  const [transitionPhase, setTransitionPhase] = useState("idle");
  const [transitionDirection, setTransitionDirection] = useState(1);
  const [markerPulse, setMarkerPulse] = useState(false);
  const [cueVisualEntered, setCueVisualEntered] = useState(false);

  const navigationTimerRef = useRef(null);
  const transitionTimerRef = useRef(null);
  const markerTimerRef = useRef(null);
  const progressPointerTypeRef = useRef("mouse");
  const previousSegmentIdRef = useRef(Number(segment?.id ?? 0));
  const pendingDirectionRef = useRef(1);

  useEffect(() => {
    if (!fitViewport || typeof window === "undefined") return undefined;

    let frame = null;
    const updateViewportWidth = () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        setViewportWidth(
        Math.round(window.visualViewport?.width || window.innerWidth)
      );
      });
    };

    updateViewportWidth();
    window.addEventListener("resize", updateViewportWidth, { passive: true });

    return () => {
      if (frame != null) window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", updateViewportWidth);
    };
  }, [fitViewport]);

  const wordDensity = useMemo(
    () =>
      getAdaptiveWordDensity({
        words: segment?.words,
        fitViewport,
        compactViewport,
        viewportWidth,
        viewportHeight,
      }),
    [
      segment?.words,
      fitViewport,
      compactViewport,
      viewportWidth,
      viewportHeight,
    ]
  );

  const densityStyles = WORD_DENSITY_STYLES[wordDensity];
  const compactProgress = fitViewport && viewportWidth < 640;

  const normalizedSelection = useMemo(() => {
    if (selectionStart == null || selectionEnd == null) return null;

    return {
      start: Math.min(selectionStart, selectionEnd),
      end: Math.max(selectionStart, selectionEnd),
    };
  }, [selectionStart, selectionEnd]);

  const clearSelection = () => {
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
    setShowSelectionMenu(false);
  };

  const playSelectedRange = () => {
    if (!normalizedSelection || !segment?.words?.length) return;

    const { start, end } = normalizedSelection;

    handleRangePlay?.({
      startIndex: start,
      endIndex: end,
    });
  };

  const handleWordMouseDown = (index, e) => {
    if (!enableWordActions || practiceMode === "shadowing") return;

    e.preventDefault();
    setIsSelecting(true);
    setSelectionStart(index);
    setSelectionEnd(index);
    setShowSelectionMenu(false);
  };

  const handleWordMouseEnter = (index) => {
    if (!isSelecting) return;
    setSelectionEnd(index);
  };

  const handleWordMouseUp = () => {
    if (!isSelecting) return;

    setIsSelecting(false);

    if (
      selectionStart != null &&
      selectionEnd != null &&
      selectionStart !== selectionEnd
    ) {
      setShowSelectionMenu(true);
    }
  };

  useEffect(() => {
    return () => {
      if (navigationTimerRef.current != null) {
        window.clearTimeout(navigationTimerRef.current);
      }

      if (transitionTimerRef.current != null) {
        window.clearTimeout(transitionTimerRef.current);
      }

      if (markerTimerRef.current != null) {
        window.clearTimeout(markerTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!showCenterCue) {
      setCueVisualEntered(false);
      return;
    }

    setCueVisualEntered(false);

    const frame = window.requestAnimationFrame(() => {
      setCueVisualEntered(true);
    });

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [showCenterCue]);

  useEffect(() => {
    clearSelection();
  }, [segment?.id]);

  useEffect(() => {
    if (!enableWordActions || !showWords) clearSelection();
  }, [enableWordActions, showWords]);

  useEffect(() => {
    const handleGlobalMouseUp = () => {
      if (isSelecting) {
        setIsSelecting(false);

        if (
          selectionStart != null &&
          selectionEnd != null &&
          selectionStart !== selectionEnd
        ) {
          setShowSelectionMenu(true);
        }
      }
    };

    const handleDocumentMouseDown = (e) => {
      if (!selectionAreaRef.current) return;

      if (!selectionAreaRef.current.contains(e.target)) {
        clearSelection();
      }
    };

    document.addEventListener("mouseup", handleGlobalMouseUp);
    document.addEventListener("mousedown", handleDocumentMouseDown);

    return () => {
      document.removeEventListener("mouseup", handleGlobalMouseUp);
      document.removeEventListener("mousedown", handleDocumentMouseDown);
    };
  }, [isSelecting, selectionStart, selectionEnd]);

  const mappedAssessmentWords = useMemo(() => {
    const assessmentWords = assessment?.pronunciation?.NBest?.[0]?.Words || [];
    return buildSegmentAssessmentMap(segment?.words || [], assessmentWords);
  }, [segment?.words, assessment]);

  useEffect(() => {
    const nextSegmentId = Number(segment?.id ?? 0);
    const previousSegmentId = previousSegmentIdRef.current;

    if (nextSegmentId === previousSegmentId) {
      return;
    }

    const inferredDirection =
      nextSegmentId > previousSegmentId ? 1 : -1;
    const direction =
      pendingDirectionRef.current || inferredDirection;

    previousSegmentIdRef.current = nextSegmentId;
    setTransitionDirection(direction);
    setTransitionPhase("enter-prep");

    const frame = window.requestAnimationFrame(() => {
      setTransitionPhase("enter");
    });

    if (transitionTimerRef.current != null) {
      window.clearTimeout(transitionTimerRef.current);
    }

    transitionTimerRef.current = window.setTimeout(() => {
      setTransitionPhase("idle");
      transitionTimerRef.current = null;
    }, 185);

    return () => {
      window.cancelAnimationFrame(frame);
    };
  }, [segment?.id]);

  const isFirstSegment = Number(segment?.id ?? 0) <= 0;
  const isLastSegment =
    Number(segment?.id ?? 0) >= Math.max(0, segments.length - 1);

  const currentSentence = Number(segment?.id ?? 0) + 1;
  const totalSentences = Math.max(1, segments.length);
  const useProgressSegments = totalSentences <= 12;
  const canSelectSentence =
    typeof handleSelectSegment === "function";

  const sentencePosition =
    totalSentences <= 1
      ? 0
      : ((currentSentence - 1) / (totalSentences - 1)) * 100;

  const previewPosition =
    previewSentence == null || totalSentences <= 1
      ? null
      : ((previewSentence - 1) / (totalSentences - 1)) * 100;

  // Short exercises show one node per sentence. Long exercises use evenly
  // spaced range ticks over the same continuous timeline while pointer
  // position still resolves to the exact sentence.
  const timelineTickCount = Math.min(20, totalSentences);

  const previousSentencePreview = useMemo(
    () => getSegmentPreview(segments?.[currentSentence - 2]),
    [segments, currentSentence]
  );

  const nextSentencePreview = useMemo(
    () => getSegmentPreview(segments?.[currentSentence]),
    [segments, currentSentence]
  );

  const hoveredSentencePreview = useMemo(
    () =>
      previewSentence == null ||
      previewSentence === currentSentence
        ? ""
        : getSegmentPreview(
            segments?.[previewSentence - 1],
            10
          ),
    [segments, previewSentence, currentSentence]
  );

  const sentenceCounter =
    `Sentence ${currentSentence} of ${totalSentences}`;

  const clampSentenceNumber = (value) => {
    return Math.min(
      totalSentences,
      Math.max(1, Math.round(Number(value) || 1))
    );
  };

  const confirmMarkerSelection = () => {
    setMarkerPulse(true);

    if (markerTimerRef.current != null) {
      window.clearTimeout(markerTimerRef.current);
    }

    markerTimerRef.current = window.setTimeout(() => {
      setMarkerPulse(false);
      markerTimerRef.current = null;
    }, 320);
  };

  const navigateToSentence = (
    sentenceNumber,
    { confirmMarker = false } = {}
  ) => {
    const nextSentence = clampSentenceNumber(sentenceNumber);

    if (nextSentence === currentSentence) {
      return;
    }

    const direction =
      nextSentence > currentSentence ? 1 : -1;

    clearSelection();
    setPreviewSentence(null);
    setTransitionDirection(direction);
    setTransitionPhase("exit");
    pendingDirectionRef.current = direction;

    if (confirmMarker) {
      confirmMarkerSelection();
    }

    if (navigationTimerRef.current != null) {
      window.clearTimeout(navigationTimerRef.current);
    }

    navigationTimerRef.current = window.setTimeout(() => {
      if (canSelectSentence) {
        handleSelectSegment(nextSentence - 1);
      } else if (
        direction < 0 &&
        nextSentence === currentSentence - 1
      ) {
        handlePreviousSegment?.();
      } else if (
        direction > 0 &&
        nextSentence === currentSentence + 1
      ) {
        handleNextSegment?.();
      }

      navigationTimerRef.current = null;
    }, 72);
  };

  const sentenceFromPointer = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();

    if (!rect.width) {
      return currentSentence;
    }

    const ratio = Math.min(
      1,
      Math.max(0, (event.clientX - rect.left) / rect.width)
    );

    return clampSentenceNumber(
      1 + ratio * Math.max(0, totalSentences - 1)
    );
  };

  const handleContinuousProgressKeyDown = (event) => {
    if (!canSelectSentence) return;

    let nextSentence = null;

    if (event.key === "ArrowLeft") {
      nextSentence = currentSentence - 1;
    } else if (event.key === "ArrowRight") {
      nextSentence = currentSentence + 1;
    } else if (event.key === "Home") {
      nextSentence = 1;
    } else if (event.key === "End") {
      nextSentence = totalSentences;
    } else if (event.key === "PageUp") {
      nextSentence = currentSentence - 10;
    } else if (event.key === "PageDown") {
      nextSentence = currentSentence + 10;
    }

    if (nextSentence == null) return;

    event.preventDefault();
    event.stopPropagation();
    navigateToSentence(nextSentence, { confirmMarker: true });
  };

  const handleStageKeyDown = (event) => {
    const targetTag = event.target?.tagName?.toLowerCase();

    if (
      targetTag === "button" ||
      targetTag === "input" ||
      targetTag === "textarea" ||
      targetTag === "select"
    ) {
      return;
    }

    if (event.key === "ArrowLeft" && !isFirstSegment) {
      event.preventDefault();
      navigateToSentence(currentSentence - 1);
    }

    if (event.key === "ArrowRight" && !isLastSegment) {
      event.preventDefault();
      navigateToSentence(currentSentence + 1);
    }
  };

  const renderSentenceStatus = () => {
    if (!showCenterCue) {
      const displayedSentence =
        previewSentence ?? currentSentence;
      const isPreviewing =
        previewSentence != null &&
        previewSentence !== currentSentence;

      return (
        <nav
          className={[
            "group relative grid w-[min(88vw,440px)] select-none grid-cols-[1fr_auto] items-center gap-x-2",
            compactProgress
              ? "h-[46px] grid-rows-[14px_32px]"
              : "h-[52px] grid-rows-[16px_36px]",
            "sm:flex sm:h-[52px] sm:w-[min(78vw,440px)] sm:items-start sm:gap-3",
          ].join(" ")}
          aria-label="Sentence navigation"
          onMouseLeave={() => setPreviewSentence(null)}
        >
          <span
            className={[
              "col-start-1 row-start-1 w-auto shrink-0 pl-1 text-left font-bold uppercase tracking-[0.18em] transition-colors duration-150 sm:col-auto sm:row-auto sm:flex sm:h-9 sm:w-[68px] sm:items-center sm:justify-end sm:pl-0 sm:text-right sm:text-[9px]",
              compactProgress ? "text-[8px]" : "text-[9px]",
              isPreviewing
                ? "text-blue-600"
                : "text-slate-400",
            ].join(" ")}
          >
            {isPreviewing ? "Go to" : "Sentence"}
          </span>

          {useProgressSegments ? (
            <div
              className={[
                "relative col-span-2 row-start-2 flex min-w-0 items-center sm:col-span-1 sm:row-auto sm:h-9 sm:flex-1",
                compactProgress ? "h-8" : "h-9",
              ].join(" ")}
              aria-label={sentenceCounter}
            >
              <div
                className="pointer-events-none absolute left-3 right-3 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-slate-300/95"
                aria-hidden="true"
              />

              {totalSentences > 1 && (
                <div
                  className="pointer-events-none absolute left-3 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-[width] duration-250 ease-out"
                  style={{
                    width: `calc((100% - 24px) * ${
                      (currentSentence - 1) /
                      Math.max(1, totalSentences - 1)
                    })`,
                  }}
                  aria-hidden="true"
                />
              )}

              <div className="relative flex w-full items-center justify-between">
                {Array.from(
                  { length: totalSentences },
                  (_, index) => {
                    const number = index + 1;
                    const isCurrent =
                      number === currentSentence;
                    const isPreview =
                      number === previewSentence;
                    const isComplete =
                      number < currentSentence;

                    return (
                      <button
                        key={number}
                        type="button"
                        onPointerDown={(event) => {
                          progressPointerTypeRef.current =
                            event.pointerType || "mouse";

                          if (event.pointerType !== "mouse") {
                            setPreviewSentence(null);
                          }
                        }}
                        onClick={(event) =>
                          navigateToSentence(number, {
                            confirmMarker:
                              event.detail === 0 ||
                              progressPointerTypeRef.current === "mouse",
                          })
                        }
                        onPointerEnter={(event) => {
                          if (event.pointerType === "mouse") {
                            setPreviewSentence(number);
                          }
                        }}
                        onFocus={(event) => {
                          if (event.currentTarget.matches(":focus-visible")) {
                            setPreviewSentence(number);
                          }
                        }}
                        onBlur={() =>
                          setPreviewSentence(null)
                        }
                        disabled={!canSelectSentence}
                        aria-label={`Go to sentence ${number}`}
                        aria-current={
                          isCurrent ? "step" : undefined
                        }
                        className={[
                          "group/node relative flex shrink-0 items-center justify-center rounded-full outline-none sm:h-9 sm:w-8",
                          compactProgress ? "h-8 w-7" : "h-9 w-8",
                          canSelectSentence
                            ? "cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-400/65 focus-visible:ring-offset-2"
                            : "cursor-default",
                        ].join(" ")}
                      >
                        <span
                          className={[
                            "relative block rounded-full transition-all duration-180 ease-out",
                            isCurrent
                              ? compactProgress
                                ? "h-2.5 w-2.5 border-2 border-white bg-blue-600 shadow-[0_2px_8px_rgba(37,99,235,0.32)] ring-2 ring-blue-100/80 sm:h-3 sm:w-3"
                                : "h-3 w-3 border-2 border-white bg-blue-600 shadow-[0_2px_8px_rgba(37,99,235,0.32)] ring-2 ring-blue-100/80"
                              : isPreview
                              ? compactProgress
                                ? "h-2.5 w-2.5 bg-blue-500 shadow-[0_2px_8px_rgba(59,130,246,0.30)] ring-2 ring-white sm:h-3 sm:w-3"
                                : "h-3 w-3 bg-blue-500 shadow-[0_2px_8px_rgba(59,130,246,0.30)] ring-2 ring-white"
                              : isComplete
                              ? "h-2 w-2 bg-blue-400 ring-2 ring-white"
                              : "h-2.5 w-2.5 border border-white bg-slate-300 shadow-[0_1px_3px_rgba(15,23,42,0.16)] ring-1 ring-slate-400/45 group-hover/node:h-3 group-hover/node:w-3 group-hover/node:bg-blue-300 group-hover/node:ring-blue-400/55",
                          ].join(" ")}
                          aria-hidden="true"
                        />

                        {isCurrent && markerPulse && (
                          <span
                            className="pointer-events-none absolute h-5 w-5 animate-ping rounded-full bg-blue-400/30"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    );
                  }
                )}
              </div>
            </div>
          ) : (
            <div
              role="slider"
              tabIndex={canSelectSentence ? 0 : -1}
              aria-label="Select sentence"
              aria-valuemin={1}
              aria-valuemax={totalSentences}
              aria-valuenow={currentSentence}
              aria-valuetext={sentenceCounter}
              aria-disabled={!canSelectSentence}
              onKeyDown={handleContinuousProgressKeyDown}
              onPointerDown={(event) => {
                progressPointerTypeRef.current =
                  event.pointerType || "mouse";

                if (event.pointerType !== "mouse") {
                  setPreviewSentence(null);
                }
              }}
              onPointerMove={(event) => {
                if (
                  !canSelectSentence ||
                  event.pointerType !== "mouse"
                ) {
                  return;
                }

                setPreviewSentence(
                  sentenceFromPointer(event)
                );
              }}
              onPointerLeave={() =>
                setPreviewSentence(null)
              }
              onClick={(event) => {
                if (!canSelectSentence) return;

                navigateToSentence(
                  sentenceFromPointer(event),
                  {
                    confirmMarker:
                      progressPointerTypeRef.current === "mouse",
                  }
                );
              }}
              className={[
                "relative col-span-2 row-start-2 flex min-w-0 touch-manipulation items-center outline-none sm:col-span-1 sm:row-auto sm:h-9 sm:flex-1",
                compactProgress ? "h-8" : "h-9",
                canSelectSentence
                  ? "cursor-pointer focus-visible:ring-2 focus-visible:ring-blue-400/65 focus-visible:ring-offset-2"
                  : "cursor-default",
              ].join(" ")}
            >
              <div className="relative h-5 w-full">
                <div
                  className="absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-slate-300"
                  aria-hidden="true"
                />

                <div
                  className="absolute left-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600 transition-[width] duration-200 ease-out"
                  style={{
                    width: `${sentencePosition}%`,
                  }}
                  aria-hidden="true"
                />

                <div
                  className="pointer-events-none absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-between"
                  aria-hidden="true"
                >
                  {Array.from(
                    { length: timelineTickCount },
                    (_, tickIndex) => {
                      const tickRatio =
                        timelineTickCount <= 1
                          ? 0
                          : tickIndex /
                            (timelineTickCount - 1);
                      const tickSentence =
                        1 +
                        tickRatio *
                          Math.max(
                            0,
                            totalSentences - 1
                          );
                      const isPassed =
                        tickSentence <= currentSentence;

                      return (
                        <span
                          key={tickIndex}
                          className={[
                            "block h-2 w-2 shrink-0 rounded-full border border-white shadow-[0_1px_3px_rgba(15,23,42,0.16)] ring-1 transition-colors duration-150",
                            isPassed
                              ? "bg-blue-400 ring-blue-500/35"
                              : "bg-slate-400 ring-slate-500/35",
                          ].join(" ")}
                        />
                      );
                    }
                  )}
                </div>

                {previewPosition != null &&
                  previewSentence !== currentSentence && (
                    <span
                      className="pointer-events-none absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-400 shadow-[0_2px_8px_rgba(59,130,246,0.28)]"
                      style={{
                        left: `${previewPosition}%`,
                      }}
                      aria-hidden="true"
                    />
                  )}

                <span
                  className={[
                    "pointer-events-none absolute top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-600 shadow-[0_2px_9px_rgba(37,99,235,0.34)] ring-2 ring-blue-100/80 transition-[left] duration-200 ease-out",
                    compactProgress ? "h-3 w-3" : "h-3.5 w-3.5",
                  ].join(" ")}
                  style={{
                    left: `${sentencePosition}%`,
                  }}
                  aria-hidden="true"
                />

                {markerPulse && (
                  <span
                    className="pointer-events-none absolute top-1/2 h-6 w-6 -translate-x-1/2 -translate-y-1/2 animate-ping rounded-full bg-blue-400/25"
                    style={{
                      left: `${sentencePosition}%`,
                    }}
                    aria-hidden="true"
                  />
                )}
              </div>
            </div>
          )}

          <span className="col-start-2 row-start-1 flex w-auto shrink-0 items-baseline justify-end gap-1 pr-1 whitespace-nowrap tabular-nums sm:col-auto sm:row-auto sm:h-9 sm:w-[58px] sm:items-center sm:justify-start sm:pr-0">
            <span
              key={displayedSentence}
              className={[
                "min-w-[1.35em] text-right text-sm font-bold leading-none transition-colors duration-150",
                isPreviewing
                  ? "text-blue-700"
                  : "text-slate-800",
              ].join(" ")}
            >
              {displayedSentence}
            </span>

            <span className="text-[9px] font-semibold text-slate-400">
              of
            </span>

            <span className="text-[11px] font-semibold leading-none text-slate-600">
              {totalSentences}
            </span>
          </span>

          <div
            className="pointer-events-none absolute bottom-0 left-[80px] right-[70px] hidden h-4 min-w-0 items-center justify-center overflow-hidden sm:flex"
            aria-live="polite"
          >
            <span
              key={hoveredSentencePreview}
              className={[
                "block max-w-full truncate text-center text-[11px] font-semibold leading-none transition-[opacity,transform,color] duration-200 ease-out",
                isPreviewing && hoveredSentencePreview
                  ? "translate-y-0 text-blue-700 opacity-100"
                  : "translate-y-0.5 text-slate-400 opacity-0",
              ].join(" ")}
            >
              {hoveredSentencePreview || "\u00A0"}
            </span>
          </div>
        </nav>
      );
    }

    const isShadowingGo =
      practiceMode === "shadowing" &&
      recordingStatus === "recording";

    const cuePulse =
      practiceMode === "echoing"
        ? echoingPulseCount
        : cuePulseCount;

    const cueStepColors =
      practiceMode === "echoing"
        ? ["#3b82f6", "#6366f1", "#10b981"]
        : ["#f59e0b", "#f97316", "#10b981"];

    const cueLabel =
      isShadowingGo ? "Speak now" : "Get ready";

    const cueLabelColor =
      isShadowingGo
        ? "#047857"
        : practiceMode === "echoing"
        ? "#1d4ed8"
        : "#b45309";

    return (
      <nav
        className="relative flex h-9 w-[min(78vw,440px)] select-none items-center gap-3"
        aria-label="Sentence navigation and preparation cue"
      >
        <span className="w-[68px] shrink-0 text-right text-[9px] font-bold uppercase tracking-[0.18em] text-slate-400">
          Sentence
        </span>

        <div className="relative flex h-9 min-w-0 flex-1 items-center">
          <div
            className={[
              "absolute inset-0 transition-opacity duration-500 ease-out",
              cueVisualEntered
                ? "opacity-55"
                : "opacity-100",
            ].join(" ")}
            aria-hidden="true"
          >
            {useProgressSegments ? (
              <div className="relative flex h-9 min-w-0 flex-1 items-center">
                <div className="absolute left-3 right-3 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-slate-300/95" />

                {totalSentences > 1 && (
                  <div
                    className="absolute left-3 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"
                    style={{
                      width: `calc((100% - 24px) * ${
                        (currentSentence - 1) /
                        Math.max(1, totalSentences - 1)
                      })`,
                    }}
                  />
                )}

                <div className="relative flex w-full items-center justify-between">
                  {Array.from(
                    { length: totalSentences },
                    (_, index) => {
                      const number = index + 1;
                      const isCurrent =
                        number === currentSentence;
                      const isComplete =
                        number < currentSentence;

                      return (
                        <span
                          key={number}
                          className="relative flex h-9 w-8 shrink-0 items-center justify-center"
                        >
                          <span
                            className={[
                              "block rounded-full",
                              isCurrent
                                ? "h-3 w-3 border-2 border-white bg-blue-600 shadow-[0_2px_8px_rgba(37,99,235,0.32)] ring-2 ring-blue-100/80"
                                : isComplete
                                ? "h-2 w-2 bg-blue-400 ring-2 ring-white"
                                : "h-2.5 w-2.5 border border-white bg-slate-300 ring-1 ring-slate-400/45",
                            ].join(" ")}
                          />
                        </span>
                      );
                    }
                  )}
                </div>
              </div>
            ) : (
              <div className="relative h-9 w-full">
                <div className="absolute left-0 right-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-slate-300" />

                <div
                  className="absolute left-0 top-1/2 h-0.5 -translate-y-1/2 rounded-full bg-gradient-to-r from-blue-400 to-blue-600"
                  style={{
                    width: `${sentencePosition}%`,
                  }}
                />

                <div className="absolute inset-x-0 top-1/2 flex -translate-y-1/2 items-center justify-between">
                  {Array.from(
                    { length: timelineTickCount },
                    (_, tickIndex) => {
                      const tickRatio =
                        timelineTickCount <= 1
                          ? 0
                          : tickIndex /
                            (timelineTickCount - 1);
                      const tickSentence =
                        1 +
                        tickRatio *
                          Math.max(0, totalSentences - 1);
                      const isPassed =
                        tickSentence <= currentSentence;

                      return (
                        <span
                          key={tickIndex}
                          className={[
                            "block h-2 w-2 shrink-0 rounded-full border border-white ring-1",
                            isPassed
                              ? "bg-blue-400 ring-blue-500/35"
                              : "bg-slate-400 ring-slate-500/35",
                          ].join(" ")}
                        />
                      );
                    }
                  )}
                </div>

                <span
                  className="absolute top-1/2 h-3.5 w-3.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-white bg-blue-600 shadow-[0_2px_9px_rgba(37,99,235,0.34)] ring-2 ring-blue-100/80"
                  style={{
                    left: `${sentencePosition}%`,
                  }}
                />
              </div>
            )}
          </div>

          <div
            className={[
              "absolute inset-0 flex items-center justify-center transition-[opacity,transform] duration-500 ease-out",
              cueVisualEntered
                ? "translate-y-0 opacity-100"
                : "translate-y-1 opacity-0",
            ].join(" ")}
            role="status"
            aria-live="polite"
            aria-label={
              isShadowingGo
                ? "Speak now"
                : "Get ready"
            }
          >
            <div
              className="relative z-10 flex min-w-[188px] items-center justify-center gap-3 rounded-full border px-4 py-2"
              style={{
                backgroundColor: "#ffffff",
                borderColor:
                  isShadowingGo
                    ? "#a7f3d0"
                    : practiceMode === "echoing"
                    ? "#bfdbfe"
                    : "#fde68a",
                boxShadow:
                  "0 10px 28px -16px rgba(15, 23, 42, 0.48)",
              }}
            >
              <span
                className="shrink-0 whitespace-nowrap text-[11px] font-extrabold uppercase tracking-[0.13em]"
                style={{
                  color: cueLabelColor,
                  opacity: 1,
                }}
              >
                {cueLabel}
              </span>

              <span
                className="h-4 w-px shrink-0"
                style={{ backgroundColor: "#e2e8f0" }}
                aria-hidden="true"
              />

              <span className="flex shrink-0 items-center gap-1.5">
                {[1, 2, 3].map((step) => {
                  const isActive =
                    isShadowingGo || cuePulse >= step;

                  const isCurrentStep =
                    !isShadowingGo &&
                    cuePulse === step;

                  return (
                    <span
                      key={step}
                      className="block h-2 rounded-full transition-[width,opacity,transform,box-shadow] duration-500 ease-out"
                      style={{
                        width: isActive
                          ? step === 3
                            ? "28px"
                            : "24px"
                          : "13px",
                        opacity: 1,
                        transform: isCurrentStep
                          ? "scale(1.08)"
                          : "scale(1)",
                        backgroundColor: isActive
                          ? cueStepColors[step - 1]
                          : "#cbd5e1",
                        boxShadow: isActive
                          ? step === 3
                            ? "0 0 0 3px rgba(16, 185, 129, 0.20)"
                            : "0 2px 7px rgba(15, 23, 42, 0.16)"
                          : "inset 0 0 0 1px rgba(100, 116, 139, 0.28)",
                      }}
                      aria-hidden="true"
                    />
                  );
                })}
              </span>
            </div>
          </div>
        </div>

        <span className="flex w-[58px] shrink-0 items-baseline justify-start gap-1 whitespace-nowrap tabular-nums">
          <span className="min-w-[1.35em] text-right text-sm font-bold leading-none text-slate-800">
            {currentSentence}
          </span>

          <span className="text-[9px] font-semibold text-slate-400">
            of
          </span>

          <span className="text-[11px] font-semibold leading-none text-slate-600">
            {totalSentences}
          </span>
        </span>
      </nav>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-white/50 bg-white/80 shadow-lg shadow-blue-500/5 backdrop-blur-sm">
      <div
        className={[
          "flex min-w-0 items-center justify-between gap-3 border-b border-slate-200/70 bg-gradient-to-r from-white via-slate-50/35 to-white px-4 sm:px-5",
          fitViewport ? "min-h-[50px] py-1.5" : "min-h-[58px] py-2",
        ].join(" ")}
      >
        <div className="min-w-0 flex-1 overflow-hidden">
          {showWords ? renderHeaderIdentity?.() : null}
        </div>

        <div className="flex min-w-0 shrink-0 items-center justify-end gap-1.5 pr-1 sm:gap-2">
          {showWords ? renderGuidePill?.() : null}
          {showWords ? renderSentenceSharePill?.() : null}
          {showWords ? renderCommentsPill?.() : null}

          {showWords && isMetadataNoHelp && (
            <button
              type="button"
              onClick={() => setShowExerciseHelp(true)}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center gap-1.5 rounded-full border border-slate-200/90 bg-white p-0 text-xs font-semibold text-slate-700 shadow-sm transition-[transform,background-color,border-color,box-shadow,color] duration-200 hover:-translate-y-px hover:border-indigo-200 hover:bg-indigo-50/70 hover:text-indigo-700 hover:shadow focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-400/60 focus-visible:ring-offset-2 lg:w-auto lg:px-3.5 lg:text-sm"
              title="About this course"
              aria-label="About this course"
            >
              <span aria-hidden="true">✨</span>
              <span className="hidden whitespace-nowrap lg:inline">About this course</span>
            </button>
          )}
        </div>
      </div>

      <div
        ref={selectionAreaRef}
        tabIndex={0}
        onKeyDown={handleStageKeyDown}
        className="relative overflow-hidden rounded-2xl border border-slate-200/90 bg-gradient-to-r from-blue-50/55 via-white/90 to-indigo-50/55 shadow-[0_18px_50px_-38px_rgba(30,64,175,0.55)] ring-1 ring-white/80 outline-none transition-shadow duration-200 focus-visible:ring-2 focus-visible:ring-blue-400/50 focus-visible:ring-offset-2"
        aria-label={`${sentenceCounter}. Use the left and right arrow keys to change sentence.`}
      >
        {isFirstSegment ? (
          <div
            className="absolute inset-y-0 left-0 w-12 sm:w-32"
            aria-hidden="true"
          />
        ) : (
          <button
            type="button"
            onClick={() =>
              navigateToSentence(currentSentence - 1)
            }
            aria-label="Previous sentence"
            className="group absolute inset-y-0 left-0 z-30 flex w-12 items-center justify-start px-3 text-slate-500 outline-none transition-colors duration-200 hover:text-blue-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400/55 sm:w-32 sm:justify-center sm:px-4"
          >
            <span
              className="pointer-events-none absolute inset-y-0 left-0 right-0 bg-gradient-to-r from-blue-100/70 via-blue-50/35 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
              aria-hidden="true"
            />

            <span
              className="relative text-xl leading-none transition-transform duration-200 group-hover:-translate-x-0.5 group-focus-visible:-translate-x-0.5"
              aria-hidden="true"
            >
              ‹
            </span>
            <span className="relative ml-2 hidden text-sm font-semibold sm:inline">
              Previous
            </span>

            {previousSentencePreview && (
              <span
                className="pointer-events-none absolute left-full top-1/2 ml-3 hidden w-52 -translate-y-1/2 rounded-xl border border-slate-200/90 bg-white/95 px-3 py-2 text-left opacity-0 shadow-[0_12px_32px_-18px_rgba(15,23,42,0.45)] backdrop-blur-xl transition-all duration-180 group-hover:translate-x-0 group-hover:opacity-100 group-focus-visible:opacity-100 sm:block"
                role="tooltip"
              >
                <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Previous sentence
                </span>
                <span className="mt-0.5 block truncate text-xs font-medium text-slate-700">
                  {previousSentencePreview}
                </span>
              </span>
            )}
          </button>
        )}

        {isLastSegment ? (
          <div
            className="absolute inset-y-0 right-0 w-12 sm:w-32"
            aria-hidden="true"
          />
        ) : (
          <button
            type="button"
            onClick={() =>
              navigateToSentence(currentSentence + 1)
            }
            aria-label="Next sentence"
            className="group absolute inset-y-0 right-0 z-30 flex w-12 items-center justify-end px-3 text-slate-500 outline-none transition-colors duration-200 hover:text-blue-700 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-blue-400/55 sm:w-32 sm:justify-center sm:px-4"
          >
            <span
              className="pointer-events-none absolute inset-y-0 left-0 right-0 bg-gradient-to-l from-blue-100/70 via-blue-50/35 to-transparent opacity-0 transition-opacity duration-200 group-hover:opacity-100 group-focus-visible:opacity-100"
              aria-hidden="true"
            />

            <span className="relative mr-2 hidden text-sm font-semibold sm:inline">
              Next
            </span>
            <span
              className="relative text-xl leading-none transition-transform duration-200 group-hover:translate-x-0.5 group-focus-visible:translate-x-0.5"
              aria-hidden="true"
            >
              ›
            </span>

            {nextSentencePreview && (
              <span
                className="pointer-events-none absolute right-full top-1/2 mr-3 hidden w-52 -translate-y-1/2 rounded-xl border border-slate-200/90 bg-white/95 px-3 py-2 text-left opacity-0 shadow-[0_12px_32px_-18px_rgba(15,23,42,0.45)] backdrop-blur-xl transition-all duration-180 group-hover:opacity-100 group-focus-visible:opacity-100 sm:block"
                role="tooltip"
              >
                <span className="block text-[9px] font-bold uppercase tracking-[0.14em] text-slate-400">
                  Next sentence
                </span>
                <span className="mt-0.5 block truncate text-xs font-medium text-slate-700">
                  {nextSentencePreview}
                </span>
              </span>
            )}
          </button>
        )}

        <div
          className={[
            "relative z-10 transition-[padding,min-height] duration-200",
            densityStyles.stage,
          ].join(" ")}
          data-word-density={wordDensity}
        >
          <div className="absolute left-1/2 top-1.5 z-40 -translate-x-1/2">
            {renderSentenceStatus()}
          </div>

        {enableWordActions &&
          showWords &&
          showSelectionMenu &&
          normalizedSelection &&
          practiceMode !== "shadowing" && (
            <div className="absolute left-1/2 top-[50px] -translate-x-1/2 z-50 pointer-events-none">
              <div className="pointer-events-auto flex items-center gap-2 px-2 py-2 rounded-2xl bg-slate-900/95 text-white shadow-2xl border border-slate-700">
                <button
                  type="button"
                  onClick={playSelectedRange}
                  className="px-3 py-1.5 rounded-xl bg-blue-500 hover:bg-blue-600 text-sm font-medium transition whitespace-nowrap"
                >
                  ▶ Play selected
                </button>

                <button
                  type="button"
                  onClick={clearSelection}
                  className="px-3 py-1.5 rounded-xl bg-white text-gray-700 hover:bg-slate-100 text-sm font-medium transition whitespace-nowrap border border-slate-300 shadow-sm"
                >
                  Clear
                </button>
              </div>
            </div>
          )}

          {showWordLane ? (
            <div
              className={[
                "transition-[opacity,transform] duration-[180ms] ease-out will-change-transform",
                transitionPhase === "exit"
                  ? transitionDirection > 0
                    ? "-translate-x-2 opacity-0"
                    : "translate-x-2 opacity-0"
                  : transitionPhase === "enter-prep"
                  ? transitionDirection > 0
                    ? "translate-x-2 opacity-0"
                    : "-translate-x-2 opacity-0"
                  : "translate-x-0 opacity-100",
              ].join(" ")}
            >
        {showWords ? (
        <div
          className={[
            "flex flex-wrap items-center justify-center",
            densityStyles.lane,
          ].join(" ")}
          aria-label={`Sentence words, ${wordDensity} spacing`}
        >
          {segment?.words?.map((item, i) => {
            const matchedAssessment = mappedAssessmentWords[i];
            const pronunciation = matchedAssessment?.PronunciationAssessment;
            const phonemes = matchedAssessment?.Phonemes || [];
            const score = pronunciation?.AccuracyScore;
            const errorType = pronunciation?.ErrorType;

            const isActive = currentWord === i;

            const isShadowCueWord =
              practiceMode === "shadowing" &&
              isCueActive &&
              cuePulseCount >= 1 &&
              i === 0;

            const isInSelection =
              normalizedSelection &&
              i >= normalizedSelection.start &&
              i <= normalizedSelection.end;

            const isMultiSelection =
              normalizedSelection &&
              normalizedSelection.start !== normalizedSelection.end;

            const onWordClick = !enableWordActions
              ? undefined
              : practiceMode === "shadowing"
              ? () => handleWordPlay(i, item)
              : (e) => {
                  if (isMultiSelection) return;
                  openWordMenu(e, item.text, i);
                };

            const assessmentClass = getWordAssessmentClasses(score, errorType);

            const idleClass = [
              assessmentClass,
              "shadow-sm hover:-translate-y-px hover:border-blue-300 hover:bg-blue-50/60 hover:shadow-md",
            ].join(" ");
            const activeClass = getActiveRingClass(practiceMode);

            const selectionClass =
              "bg-gradient-to-r from-green-300 to-green-300 text-white font-semibold border border-green-500 shadow-lg shadow-green-300/30 scale-105";



            return (
              <span
                key={i}
                data-word-index={i}
                onClick={onWordClick}
                onMouseDown={
                  enableWordActions ? (e) => handleWordMouseDown(i, e) : undefined
                }
                onMouseEnter={
                  enableWordActions ? () => handleWordMouseEnter(i) : undefined
                }
                onMouseUp={enableWordActions ? handleWordMouseUp : undefined}
                className={[
                  "relative flex cursor-pointer select-none flex-col items-center overflow-hidden border font-medium leading-tight will-change-transform transition-[transform,box-shadow,border-color,background-color] duration-180",
                  densityStyles.card,
                  isInSelection
                    ? selectionClass
                    : isActive
                    ? `${idleClass} ${activeClass}`
                    : idleClass,
                ].join(" ")}
                title={
                  enableWordActions && matchedAssessment
                    ? `${matchedAssessment?.Word || item.text}${
                        typeof score === "number" ? ` • Score: ${score}` : ""
                      }${errorType ? ` • ${errorType}` : ""}`
                    : practiceMode === "shadowing"
                    ? "Tap to play from here"
                    : "Drag to select words"
                }
              >
                {(phonemes.length > 0 || matchedAssessment) && (
                  <div
                    className={[
                      "flex items-center justify-center",
                      densityStyles.indicator,
                    ].join(" ")}
                  >
                    {phonemes.length > 0 ? (
                      phonemes.map((ph, idx) => (
                        <span
                          key={idx}
                          className={`${densityStyles.phonemeDot} rounded-full ${getPhonemeDotColor(
                            ph?.PronunciationAssessment?.AccuracyScore
                          )} ring-1 ring-white shadow-sm`}
                          title={`${ph?.Phoneme || "phoneme"}: ${
                            ph?.PronunciationAssessment?.AccuracyScore ?? "N/A"
                          }`}
                        />
                      ))
                    ) : (
                      <span
                        className={`${densityStyles.scoreDot} rounded-full ${getWordScoreColor(
                          score,
                          errorType
                        )} ring-1 ring-white shadow-sm`}
                      />
                    )}
                  </div>
                )}

                <span className="text-gray-800 text-center leading-tight break-words">
                  {item.text}
                </span>

                {isShadowCueWord && (
                  <div className="absolute inset-0 bg-white/20 animate-ping rounded-xl" />
                )}
              </span>
            );
          })}
        </div>
        ) : (
          <div
            className={[
              "flex flex-wrap items-center justify-center",
              densityStyles.hiddenLane,
            ].join(" ")}
            aria-label="Sentence words are hidden"
          >
            {segment?.words?.map((item, i) => {
              const isActive = isPlayingSegment && currentWord === i;
              const estimatedWidth = Math.max(
                densityStyles.hiddenMinWidth,
                Math.min(
                  densityStyles.hiddenMaxWidth,
                  String(item?.text || "").length *
                    densityStyles.hiddenCharWidth +
                    densityStyles.hiddenPadding
                )
              );

              return (
                <span
                  key={i}
                  aria-hidden="true"
                  style={{ width: `${estimatedWidth}px` }}
                  className={[
                    `${densityStyles.hiddenCard} border transition-all duration-300`,
                    "bg-white/80 border-slate-200/90 ring-1 ring-white",
                    isActive
                      ? getActiveRingClass("echoing")
                      : "shadow-sm",
                  ].join(" ")}
                />
              );
            })}
          </div>
        )}
            </div>
          ) : (
            <div className="min-h-[28px]" aria-hidden="true" />
          )}
        </div>
      </div>
    </div>
  );
};

export default SentencePracticeBlock;